import { Plugin } from '@ckeditor/ckeditor5-core';
import { FocusCycler, LabeledFieldView, ViewCollection, createLabeledInputText } from '@ckeditor/ckeditor5-ui';

import './theme/rocMediaEmbedTitle.css';

export default class RocMediaEmbedTitleUI extends Plugin {
	static get pluginName() {
		return 'RocMediaEmbedTitleUI';
	}

	static get requires() {
		return ['MediaEmbedUI'];
	}

	init() {
		const mediaEmbedUI = this.editor.plugins.get('MediaEmbedUI');

		if (mediaEmbedUI._rocMediaEmbedTitleUIPatched) {
			return;
		}

		mediaEmbedUI._rocMediaEmbedTitleUIPatched = true;

		if (typeof mediaEmbedUI._showDialog !== 'function' || typeof mediaEmbedUI._handleSubmitForm !== 'function') {
			console.warn('RocMediaEmbedTitleUI: unsupported MediaEmbedUI internals.');
			return;
		}

		const originalShowDialog = mediaEmbedUI._showDialog.bind(mediaEmbedUI);

		mediaEmbedUI._showDialog = () => {
			originalShowDialog();

			const formView = mediaEmbedUI._formView;

			if (formView) {
				this._extendMediaEmbedDialog(formView);
			}
		};
		mediaEmbedUI._handleSubmitForm = () => {
			const editor = this.editor;
			const formView = mediaEmbedUI._formView;
			const dialog = editor.plugins.get('Dialog');

			if (!formView?.isValid?.()) {
				return;
			}

			const titleValue = formView.rocTitleInputView?.fieldView?.element?.value?.trim() ?? '';

			editor.execute('mediaEmbed', formView.url);
			this._applyTitleToSelectedMediaWithValue(titleValue);

			dialog.hide();
			editor.editing.view.focus();
		};
	}

	_extendMediaEmbedDialog(formView) {
		const editor = this.editor;
		const t = editor.t;

		if (formView.rocTitleInputView) {
			this._syncDialogInputsWithSelection(formView);
			this._bindTitleTabNavigation(formView);
			return;
		}

		const titleInputView = new LabeledFieldView(editor.locale, createLabeledInputText);
		titleInputView.label = t('Iframe title');
		titleInputView.infoText = t('Optional: sets the iframe title attribute.');

		formView.registerChild(titleInputView);

		// Insert after the URL field.
		const urlElement = formView.urlInputView?.element;
		if (formView.element && urlElement) {
			formView.element.insertBefore(titleInputView.element, urlElement.nextSibling);
		} else {
			formView.once('render', () => {
				const urlEl = formView.urlInputView?.element;
				if (urlEl) {
					formView.element.insertBefore(titleInputView.element, urlEl.nextSibling);
				} else {
					formView.element.appendChild(titleInputView.element);
				}
			});
		}

		formView.rocTitleInputView = titleInputView;

		if (formView.element) {
			formView.element.classList.add('ck-media-form--with-iframe-title');
		} else {
			formView.once('render', () => {
				formView.element.classList.add('ck-media-form--with-iframe-title');
			});
		}

		this._syncDialogInputsWithSelection(formView);
		this._bindTitleTabNavigation(formView);
	}

	_bindTitleTabNavigation(formView) {
		const titleInputView = formView.rocTitleInputView;
		const titleEl = titleInputView?.fieldView?.element;

		if (!titleInputView || !titleEl) {
			return;
		}

		if (formView.focusTracker && !formView._rocTitleFocusTracked) {
			formView.focusTracker.add(titleInputView.element);
			formView._rocTitleFocusTracked = true;
		}

		if (!formView._rocTitleFocusCyclerBound) {
			formView._rocTitleFocusables = new ViewCollection();
			formView._rocTitleFocusables.add(formView.urlInputView);
			formView._rocTitleFocusables.add(titleInputView);

			formView.focusCycler = new FocusCycler({
				focusables: formView._rocTitleFocusables,
				focusTracker: formView.focusTracker,
				keystrokeHandler: formView.keystrokes,
				actions: {
					focusPrevious: 'shift + tab',
					focusNext: 'tab',
				},
			});

			const originalFocus = formView.focus.bind(formView);
			formView.focus = (direction = 1) => {
				if (direction < 0) {
					formView.focusCycler.focusLast();
				} else {
					formView.focusCycler.focusFirst();
				}
			};

			formView._rocOriginalFocus = originalFocus;
			formView._rocTitleFocusCyclerBound = true;

			const dialogFocusCycler = this.editor.plugins.get('Dialog')?.view?._focusCycler;
			if (dialogFocusCycler) {
				dialogFocusCycler.chain(formView.focusCycler);
			}
		}
	}

	_syncDialogInputsWithSelection(formView) {
		const selectedElement = this.editor.model.document.selection.getSelectedElement();
		const selectedUrl =
			selectedElement && selectedElement.is('element', 'media') ? selectedElement.getAttribute('url') || '' : '';
		const selectedTitle =
			selectedElement && selectedElement.is('element', 'media')
				? selectedElement.getAttribute('title') || ''
				: '';

		this._setFieldViewValue(formView.urlInputView?.fieldView, selectedUrl);
		this._setFieldViewValue(formView.rocTitleInputView?.fieldView, selectedTitle);
		formView.mediaURLInputValue = selectedUrl;
	}

	_setFieldViewValue(fieldView, value) {
		if (!fieldView) {
			return;
		}

		fieldView.value = value;

		if (fieldView.element && fieldView.element.value !== value) {
			fieldView.element.value = value;
		}
	}

	_applyTitleToSelectedMedia(formView) {
		const titleValue = formView.rocTitleInputView.fieldView.element.value.trim();
		this._applyTitleToSelectedMediaWithValue(titleValue);
	}

	_applyTitleToSelectedMediaWithValue(titleValue) {
		const selectedElement = this.editor.model.document.selection.getSelectedElement();

		if (!selectedElement || !selectedElement.is('element', 'media')) {
			return;
		}

		this.editor.model.change((writer) => {
			if (titleValue) {
				writer.setAttribute('title', titleValue, selectedElement);
			} else {
				writer.removeAttribute('title', selectedElement);
			}
		});
	}
}
